import { Box, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import React, {
    useEffect,
    useState,
    useRef,
    useCallback,
    useMemo
} from 'react';
import UserAvatar from '../components/UserAvatar';
import MessageBox from '../components/MessageBox';
import ChatDetails from '../components/ChatDetails';
import {
    useGetChatProfileQuery,
    useGetChatUserQuery,
    useGetChatMessagesQuery,
    useReadChatMessageMutation
} from '@store/feature/chat/chatApi';
import SendChat from '../components/SendChat';
import { FiCommand } from 'react-icons/fi';
import { FaArrowDown } from 'react-icons/fa6';
import Typing from '../../../../components/Typing';
import { useMediaQuery } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PrivateKnowledgeBase from '../components/privateKnowledgeBase/PrivateKnowledgeBaseModal';
import { useParams } from 'react-router-dom';

const ChatMessageContent = () => {
    const { id } = useParams();
    const [before, setBefore] = useState(null);
    const [showChatDetails, setShowChatDetails] = useState(false);
    const [allMessages, setAllMessages] = useState([]);
    const [isThinking, setIsThinking] = useState(false);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);

    // knowledge base modal
    const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
    const handleShowKnowledgeBaseModal = () => {
        setShowKnowledgeBaseModal(!showKnowledgeBaseModal);
    };
    const handleKnowledgeBaseModalClose = () => {
        setShowKnowledgeBaseModal(false);
    };

    // Refs
    const bottomRef = useRef(null);
    const messagesWrapperRef = useRef(null);
    const topMessageRef = useRef(null);
    const hasInitiallyScrolled = useRef(false);
    const shouldScrollToBottom = useRef(false);
    const isNewChatSession = useRef(false);
    const isPaginationLocked = useRef(false);
    const suppressAutoScrollUntilRef = useRef(0);
    const wasAtBottomRef = useRef(true);

    useEffect(() => {
        hasInitiallyScrolled.current = false;
        shouldScrollToBottom.current = false;
        isNewChatSession.current = true;
        isPaginationLocked.current = false;
        setShowScrollButton(false);
    }, [id]);

    const isSmallScreen = useMediaQuery('(max-width: 1080px)');

    // API hooks
    const { data: profile } = useGetChatProfileQuery();
    const { data: userProfile, isLoading, error } = useGetChatUserQuery(id);
    const [readMessage] = useReadChatMessageMutation();

    const { data: page, refetch } = useGetChatMessagesQuery(
        {
            userId: id,
            before: before,
            limit: 20
        },
        {
            refetchOnMountOrArgChange: true,
            pollingInterval: 3000
        }
    );

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior });
        }
    }, []);

    const isAtBottom = useCallback(() => {
        if (!messagesWrapperRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } =
            messagesWrapperRef.current;
        return scrollHeight - scrollTop - clientHeight <= 50;
    }, []);

    const handleScroll = useCallback(() => {
        const atBottom = isAtBottom();
        setShowScrollButton(!atBottom && allMessages.length > 0);
    }, [isAtBottom, allMessages.length]);

    const formatDate = useCallback((timestampOrIso) => {
        const date =
            typeof timestampOrIso === 'number'
                ? new Date(timestampOrIso * 1000)
                : new Date(timestampOrIso);

        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const messageDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate()
        );
        const todayDate = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );
        const yesterdayDate = new Date(
            yesterday.getFullYear(),
            yesterday.getMonth(),
            yesterday.getDate()
        );

        if (messageDate.getTime() === todayDate.getTime()) {
            return 'Today';
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
            return 'Yesterday';
        } else {
            const diffTime = todayDate.getTime() - messageDate.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays <= 7 && diffDays > 0) {
                return date.toLocaleDateString('en-US', { weekday: 'long' });
            } else {
                return date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year:
                        messageDate.getFullYear() !== todayDate.getFullYear()
                            ? 'numeric'
                            : undefined
                });
            }
        }
    }, []);

    const prevAfterRef = useRef(page?.after);
    useEffect(() => {
        if (
            page?.after &&
            prevAfterRef.current &&
            page.after !== prevAfterRef.current
        ) {
            setBefore(null);
        }
        prevAfterRef.current = page?.after;
    }, [page?.after]);

    const prevIdRef = useRef(id);
    useEffect(() => {
        if (prevIdRef.current && prevIdRef.current !== id) {
            setAllMessages([]);
            setBefore(null);
            setShowChatDetails(false);
            setIsThinking(false);
            setShowScrollButton(false);
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(true);

            hasInitiallyScrolled.current = false;
            shouldScrollToBottom.current = false;
            isNewChatSession.current = true;
            isPaginationLocked.current = false;

            refetch();
        }
        prevIdRef.current = id;
    }, [id, refetch]);

    // Merge new messages
    useEffect(() => {
        if (before === null) {
            wasAtBottomRef.current = isAtBottom();
        }

        if (!page?.items || !Array.isArray(page.items)) {
            setIsLoadingOlderMessages(false);
            isPaginationLocked.current = false;
            return;
        }

        if (page.items.length === 0) {
            setIsLoadingOlderMessages(false);
            setHasMoreMessages(false);
            isPaginationLocked.current = false;
            return;
        }

        const ascending = [...page.items].reverse();
        const hasMore = page.items.length >= 20;
        setHasMoreMessages(hasMore);

        if (before === null) {
            setAllMessages((prev) => {
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = ascending.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );
                const filteredPrev = prev.filter((msg) => {
                    if (msg.uuid && msg.uuid.startsWith('temp-')) {
                        return !newUniqueMessages.some(
                            (nm) =>
                                nm.body === msg.body &&
                                nm.from === msg.from &&
                                nm.to === msg.to &&
                                JSON.stringify(nm.files) ===
                                    JSON.stringify(msg.files)
                        );
                    }
                    return !(
                        msg.status === 'sent' &&
                        msg.from === profile?.uuid &&
                        newUniqueMessages.some((nm) => nm.uuid === msg.uuid)
                    );
                });

                if (newUniqueMessages.length === 0) {
                    if (!isLoadingOlderMessages) {
                        setIsLoadingOlderMessages(false);
                    }
                    return filteredPrev;
                }

                const updatedMessages = [...filteredPrev, ...newUniqueMessages];
                return updatedMessages;
            });
            if (!isLoadingOlderMessages) {
                setIsLoadingOlderMessages(false);
            }
        } else {
            const wrapper = messagesWrapperRef.current;
            let prevScrollHeight = wrapper ? wrapper.scrollHeight : 0;
            setAllMessages((prev) => {
                const existingIds = new Set(prev?.map((msg) => msg.uuid));
                const newUniqueMessages = ascending.filter(
                    (msg) => !existingIds.has(msg.uuid)
                );
                if (newUniqueMessages.length === 0) return prev;
                return [...newUniqueMessages, ...prev];
            });
            setTimeout(() => {
                if (wrapper) {
                    wrapper.scrollTop =
                        wrapper.scrollHeight -
                        prevScrollHeight +
                        wrapper.scrollTop;
                }
                setIsLoadingOlderMessages(false);
                isPaginationLocked.current = false;
                suppressAutoScrollUntilRef.current = Date.now() + 2000; // Longer suppression
            }, 100);
        }
    }, [
        page?.items,
        before,
        page?.after,
        page?.total_count,
        profile?.uuid,
        isLoadingOlderMessages
    ]);

    // SINGLE UNIFIED AUTO-SCROLL LOGIC
    useEffect(() => {
        if (!allMessages.length) return;

        // Stronger suppression guard
        if (
            isPaginationLocked.current ||
            isLoadingOlderMessages ||
            before !== null ||
            Date.now() < suppressAutoScrollUntilRef.current
        ) {
            return;
        }

        const lastMessage = allMessages[allMessages.length - 1];
        const isOwnMessage = lastMessage?.from === profile?.uuid;

        //  Scroll if flagged explicitly (e.g., after sending)
        if (shouldScrollToBottom.current) {
            setTimeout(() => {
                scrollToBottom('smooth');
                shouldScrollToBottom.current = false;
            }, 80);
            return;
        }

        //  Always scroll if it's my own message (unless paginating)
        if (isOwnMessage) {
            setTimeout(() => scrollToBottom('smooth'), 80);
            return;
        }

        //  Scroll if user was at bottom before new messages arrived (only if not paginating)
        if (wasAtBottomRef.current && before === null) {
            setTimeout(() => scrollToBottom('smooth'), 80);
            wasAtBottomRef.current = false;
            return;
        }

        setTimeout(() => {
            if (
                isAtBottom() &&
                !isPaginationLocked.current &&
                !isLoadingOlderMessages &&
                before === null
            ) {
                scrollToBottom('smooth');
                return;
            }
        }, 50);

        if (isNewChatSession.current && !hasInitiallyScrolled.current) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    if (bottomRef.current) {
                        bottomRef.current.scrollIntoView({ behavior: 'auto' });
                        hasInitiallyScrolled.current = true;
                        setTimeout(() => {
                            isNewChatSession.current = false;
                        }, 500);
                    }
                }, 100);
            });
        }
    }, [
        allMessages,
        scrollToBottom,
        isAtBottom,
        profile?.uuid,
        isLoadingOlderMessages,
        before
    ]);

    // Ensure we scroll to bottom on the first time messages are loaded
    const prevMessagesCountRef = useRef(0);
    useEffect(() => {
        const prevCount = prevMessagesCountRef.current;
        const currentCount = allMessages.length;

        if (prevCount === 0 && currentCount > 0) {
            if (isPaginationLocked.current || isLoadingOlderMessages) {
                prevMessagesCountRef.current = currentCount;
                return;
            }

            const tryInitialScroll = () => {
                if (!bottomRef.current || !messagesWrapperRef.current) return;
                const { scrollHeight, clientHeight } =
                    messagesWrapperRef.current;
                if (scrollHeight <= clientHeight) {
                    bottomRef.current.scrollIntoView({ behavior: 'auto' });
                } else {
                    bottomRef.current.scrollIntoView({ behavior: 'auto' });
                    setTimeout(
                        () =>
                            bottomRef.current?.scrollIntoView({
                                behavior: 'smooth'
                            }),
                        80
                    );
                }
                hasInitiallyScrolled.current = true;
                isNewChatSession.current = false;
            };

            requestAnimationFrame(() => {
                setTimeout(tryInitialScroll, 60);
                setTimeout(tryInitialScroll, 220);
            });
        }

        prevMessagesCountRef.current = currentCount;
    }, [allMessages.length, isLoadingOlderMessages]);

    // Lazy loading for older messages using Intersection Observer
    useEffect(() => {
        if (
            !allMessages.length ||
            !topMessageRef.current ||
            isLoadingOlderMessages ||
            !hasMoreMessages
        ) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (
                    entries[0].isIntersecting &&
                    hasMoreMessages &&
                    !isLoadingOlderMessages
                ) {
                    isPaginationLocked.current = true;
                    suppressAutoScrollUntilRef.current = Date.now() + 2000;
                    setIsLoadingOlderMessages(true);

                    const oldestMessage = allMessages[0];
                    if (oldestMessage) {
                        setBefore(oldestMessage.created_at);
                    }
                }
            },
            {
                threshold: 0.1,
                rootMargin: '20px'
            }
        );

        const currentRef = topMessageRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
            observer.disconnect();
        };
    }, [allMessages, hasMoreMessages, isLoadingOlderMessages]);

    useEffect(() => {
        const wrapper = messagesWrapperRef.current;
        if (!wrapper) return;

        wrapper.addEventListener('scroll', handleScroll);
        return () => wrapper.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    useEffect(() => {
        if (id && profile?.uuid !== id && allMessages.length > 0) {
            const lastMessage = allMessages[allMessages.length - 1];
            if (
                lastMessage?.from === id &&
                ['sent', 'delivered'].includes(lastMessage.status)
            ) {
                readMessage(lastMessage.uuid);
            }
        }
    }, [id, profile, allMessages, readMessage]);

    useEffect(() => {
        if (
            userProfile?.user?.username === 'chatbot' &&
            allMessages.length > 0
        ) {
            const lastMessage = allMessages[allMessages.length - 1];
            setIsThinking(lastMessage?.from === profile?.uuid);
        } else {
            setIsThinking(false);
        }
    }, [profile, userProfile, allMessages]);

    const handleMessageSent = useCallback(
        (newMessage) => {
            if (!newMessage?.uuid) return;

            setAllMessages((prev) => {
                if (!prev.some((msg) => msg.uuid === newMessage.uuid)) {
                    const newMessages = [
                        ...prev,
                        {
                            ...newMessage,
                            from: profile?.uuid,
                            status: 'sent'
                        }
                    ];
                    requestAnimationFrame(() => {
                        if (bottomRef.current) {
                            bottomRef.current.scrollIntoView({
                                behavior: 'smooth'
                            });
                        }
                    });
                    return newMessages;
                }
                return prev;
            });

            setShowScrollButton(false);
            setBefore(null);
            isPaginationLocked.current = false;
            suppressAutoScrollUntilRef.current = 0;
            setIsLoadingOlderMessages(false);

            setTimeout(() => {
                if (bottomRef.current) {
                    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 100);

            setTimeout(() => {
                if (bottomRef.current) {
                    bottomRef.current.scrollIntoView({ behavior: 'smooth' });
                }
            }, 300);

            shouldScrollToBottom.current = true;
            refetch();
        },
        [profile?.uuid, refetch]
    );

    const messageList = useMemo(() => {
        return allMessages?.map((msg, i) => {
            const prev = allMessages[i - 1];
            const currentDate = new Date(msg.created_at).toDateString();
            const previousDate = prev
                ? new Date(prev.created_at).toDateString()
                : null;
            const showDateHeader = i === 0 || currentDate !== previousDate;

            return (
                <React.Fragment key={msg.uuid}>
                    {showDateHeader && (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                my: 3,
                                width: '100%',
                                position: 'relative'
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor:
                                        'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(8px)',
                                    borderRadius: '20px',
                                    px: 3,
                                    py: 1,
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    minWidth: '80px',
                                    textAlign: 'center'
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: '#374151',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textTransform: 'capitalize',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {formatDate(msg.created_at)}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <MessageBox
                        id={msg.uuid}
                        messageText={msg.body}
                        timestamp={msg.created_at}
                        isRight={msg.from === profile?.uuid}
                        status={msg.status}
                        files={msg.files}
                    />
                </React.Fragment>
            );
        });
    }, [allMessages, formatDate, profile?.uuid]);

    if (isLoading) {
        return (
            <Box className="flex items-center justify-center h-20 gap-4">
                <Typography
                    sx={{
                        color: 'teal',
                        fontSize: isSmallScreen ? '1rem' : '1.25rem',
                        fontWeight: 'bold'
                    }}
                >
                    Loading...
                </Typography>
                <FiCommand className="text-teal-500 text-2xl animate-spin-fast" />
            </Box>
        );
    }

    const safeUserProfile = error ? {} : userProfile;

    return (
        <Box sx={{ display: 'flex', gap: '3px' }}>
            <Modal
                open={showKnowledgeBaseModal}
                onClose={handleKnowledgeBaseModalClose}
            >
                <PrivateKnowledgeBase
                    handleClose={handleKnowledgeBaseModalClose}
                    isKnowledgeBase={true}
                />
            </Modal>
            <Box sx={{ flex: 1 }}>
                {/* Chat Header */}
                <Box
                    sx={{
                        backgroundColor: '#27485D',
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        px: '16px',
                        position: 'sticky',
                        top: 0,
                        gap: '8px',
                        borderRadius: '4px 4px 0 0',
                        justifyContent: 'space-between'
                    }}
                >
                    <div
                        className="hover:cursor-pointer flex items-center gap-3"
                        onClick={() => setShowChatDetails(!showChatDetails)}
                    >
                        <UserAvatar
                            imgSource={safeUserProfile?.user?.image_url}
                            fullName={safeUserProfile?.user?.fullname}
                            isActive={
                                safeUserProfile?.user?.status === 'active' ||
                                safeUserProfile?.user?.uuid === profile?.uuid
                            }
                        />
                        <Typography
                            sx={{
                                width: '100%',
                                display: 'flex',
                                color: 'white',
                                cursor: 'pointer'
                            }}
                            variant="body2"
                        >
                            {safeUserProfile?.user?.fullname}
                        </Typography>
                    </div>
                    {userProfile?.user?.fullname === 'Chatbot' && (
                        <Tooltip title="Private Knowledge Base">
                            <IconButton>
                                <SchoolIcon
                                    sx={{
                                        color: 'white',
                                        fontSize: '1.5rem',
                                        cursor: 'pointer'
                                    }}
                                    onClick={handleShowKnowledgeBaseModal}
                                />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>

                {/* Chat Content */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: 'calc(100dvh - 165px)'
                    }}
                >
                    {/* Messages Container */}
                    <Box
                        ref={messagesWrapperRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            pt: '12px',
                            overflowY: 'auto',
                            height: 'calc(100dvh - 265px)',
                            '&::-webkit-scrollbar': {
                                width: '8px'
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: '#f1f1f1',
                                borderRadius: '4px'
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: '#c1c1c1',
                                borderRadius: '4px',
                                '&:hover': {
                                    backgroundColor: '#a1a1a1'
                                }
                            },
                            scrollbarColor: '#c1c1c1 #f1f1f1'
                        }}
                    >
                        {isLoadingOlderMessages && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 2,
                                    gap: 1
                                }}
                            >
                                <FiCommand className="text-teal-500 text-lg animate-spin" />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'teal',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Loading older messages...
                                </Typography>
                            </Box>
                        )}

                        <div ref={topMessageRef} />

                        {allMessages.length > 0 ? (
                            <>
                                {messageList}
                                {isThinking && (
                                    <Box
                                        sx={{
                                            alignSelf: 'flex-start',
                                            pl: 2,
                                            pb: 1
                                        }}
                                    >
                                        <Typing />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box className="flex justify-center py-20">
                                <Typography>No messages available!</Typography>
                            </Box>
                        )}

                        <div ref={bottomRef} className="w-full h-[1px] mb-2" />
                    </Box>

                    {showScrollButton && (
                        <Box sx={{ position: 'relative', width: '100%' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    bottom: '8px',
                                    transform: 'translateX(-50%)',
                                    zIndex: 1000
                                }}
                            >
                                <button
                                    className="rounded-full flex justify-center items-center bg-[#2FACFE] w-8 h-8 shadow-lg hover:bg-[#1e8fd4] transition-colors"
                                    onClick={() => {
                                        shouldScrollToBottom.current = true;
                                        scrollToBottom('smooth');
                                        setShowScrollButton(false);
                                    }}
                                >
                                    <FaArrowDown color="white" />
                                </button>
                            </Box>
                        </Box>
                    )}

                    <Box>
                        {safeUserProfile?.blocked ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography>
                                    You have blocked this user.
                                </Typography>
                            </Box>
                        ) : safeUserProfile?.blocked_by_user ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography>
                                    You are blocked by this user.
                                </Typography>
                            </Box>
                        ) : (
                            <SendChat onMessageSent={handleMessageSent} />
                        )}
                    </Box>
                </Box>
            </Box>

            {showChatDetails && (
                <Box
                    sx={{
                        height: 'calc(100dvh - 101px)',
                        borderRadius: '8px',
                        width: '40%',
                        maxWidth: '356px',
                        overflow: 'hidden',
                        position: 'relative',
                        boxShadow: 'rgba(0, 0, 0, 0.2) 0px 2px 4px'
                    }}
                >
                    <ChatDetails onClose={() => setShowChatDetails(false)} />
                </Box>
            )}
        </Box>
    );
};

export default ChatMessageContent;
